import { Link } from '@tanstack/react-router';
import { NotepadTextIcon } from 'lucide-react';
import { twMerge } from 'tailwind-merge';

import { Avatar, AvatarFallback, AvatarImage, Icon, Text } from '@/_components';
import { SingleFileView } from '@/_containers/inputs/media/form-files-input';
import type { SupportConversation } from '@/_containers/support/shared/schemas/support-query-schema';
import { dateTime, nameResolver, noop } from '@/_shared';

import { freshDeskAttachmentToFile } from './helper';
import { SubHeaderSection } from './utility-components';

export function Conversation({ conversations }: { conversations: SupportConversation[] }) {
  return (
    <>
      {conversations.map(
        ({
          id,
          user,
          created_at,
          body,
          attachments,
          incoming,
          source,
          to_emails,
          cc_emails,
          bcc_emails,
          private: isPrivate,
          user_type,
        }) => {
          let link = user_type === 'contact' ? '/contacts/$id' : '/team-members/$id';
          const name = nameResolver(user?.first_name, user?.last_name, '***UNKNOWN***');
          const url = user?.profile_image?.[0]?.url;
          if (user_type === 'unknown') {
            link = '';
          }

          return (
            <article
              key={id}
              className={twMerge(
                'rounded-lg px-4 py-6',
                !incoming && 'dark:bg-gray-850 bg-[#F5F7F9]',
                isPrivate && 'bg-[#FEF0E0] dark:bg-[#FEF0E0]'
              )}
            >
              <div className="col-span-2 mb-4 flex items-start gap-3">
                <Avatar>
                  <AvatarImage src={url} />
                  <AvatarFallback name={name || '***UNKNOWN***'} className="text-sm" />
                </Avatar>
                <div className="flex flex-col gap-0.5">
                  <Text as="p" className="text-primary font-medium">
                    {user?.id ? (
                      <Link
                        to={link}
                        className="decoration-primary hover:underline"
                        params={{ id: user?.id?.toString() || '' }}
                      >
                        {name}
                      </Link>
                    ) : (
                      name
                    )}{' '}
                    <span className="text-c-card-muted">{isPrivate ? 'added a private note' : 'replied'}</span>
                  </Text>

                  {created_at && (
                    <div className={twMerge('text-c-card-muted text-xs italic', isPrivate && 'dark:text-accent-light')}>
                      {dateTime.fromNow(created_at)} ({dateTime.format(created_at, 'ddd, MMMM DD, YYYY [at] hh:mm A')})
                    </div>
                  )}
                </div>
              </div>

              {!isPrivate && (
                <SubHeaderSection
                  bcc_emails={bcc_emails ?? []}
                  source={source}
                  to_emails={to_emails ?? []}
                  cc_emails={cc_emails ?? []}
                  isConversation
                />
              )}

              <section className="flex items-start gap-3">
                <div className="flex size-8 justify-center">
                  {isPrivate && <Icon icon={NotepadTextIcon} className="text-accent dark:text-accent size-4" />}
                </div>
                <div
                  className={twMerge(
                    'text-accent __fresh-desk-body block w-full text-sm dark:text-white',
                    isPrivate && 'dark:text-accent'
                  )}
                  dangerouslySetInnerHTML={{
                    __html: body,
                  }}
                />
              </section>

              {attachments && attachments.length > 0 && (
                <section className="mt-4 grid grid-cols-2 gap-3">
                  {attachments.map((f, i) => {
                    return (
                      <div
                        key={i}
                        className={twMerge(
                          'border-c-border rounded-lg border px-3 py-2',
                          isPrivate && 'dark:border-gray-300'
                        )}
                      >
                        <SingleFileView
                          file={freshDeskAttachmentToFile(f)}
                          onRemove={noop}
                          onAdd={noop}
                          disabled={false}
                          labelClassName={twMerge(isPrivate && 'text-accent-light dark:text-accent')}
                          sizeClassName={twMerge(isPrivate && 'text-accent-light dark:text-accent-light')}
                          downloadButtonClassName={twMerge(
                            isPrivate &&
                              'text-accent dark:text-accent hover:text-accent-light dark:hover:text-accent-light opacity-100 hover:bg-black/70 dark:hover:bg-black/70'
                          )}
                        />
                      </div>
                    );
                  })}
                </section>
              )}
            </article>
          );
        }
      )}
    </>
  );
}
